/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import yargs from "yargs";
import { hideBin } from "yargs/helpers";
import { promptApplicationRid } from "./prompts/promptApplicationRid.js";
import { promptApplicationUrl } from "./prompts/promptApplicationUrl.js";
import { promptClientId } from "./prompts/promptClientId.js";
import { promptCorsProxy } from "./prompts/promptCorsProxy.js";
import { promptFoundryUrl } from "./prompts/promptFoundryUrl.js";
import { promptOntologyAndOsdkPackageAndOsdkRegistryUrl } from "./prompts/promptOntologyAndOsdkPackageAndOsdkRegistryUrl.js";
import { promptOverwrite } from "./prompts/promptOverwrite.js";
import { promptProject } from "./prompts/promptProject.js";
import { promptScopes } from "./prompts/promptScopes.js";
import { promptSdkVersion } from "./prompts/promptSdkVersion.js";
import { promptTemplate } from "./prompts/promptTemplate.js";
import { run } from "./run.js";
export async function cli(args = process.argv) {
  const base = yargs(hideBin(args)).version(process.env.PACKAGE_VERSION ?? "").wrap(Math.min(150, yargs().terminalWidth())).strict().help().command("$0 [project] [--<option>]", "Create a new OSDK application based on framework templates. Information may be provided through options to skip interactive prompts.", yargs => yargs.positional("project", {
    type: "string",
    describe: "Project name to create"
  }).option("overwrite", {
    type: "boolean",
    describe: "Overwrite project directory if already exists"
  }).option("beta", {
    type: "boolean",
    describe: "Use templates compatible with the Beta version of the SDK"
  }).option("unstableFeatures", {
    type: "boolean",
    describe: "Enable unstable/experimental features in the generated app."
  }).option("template", {
    type: "string",
    describe: "Template name to use"
  }).option("sdkVersion", {
    type: "string",
    describe: "The OSDK version to use"
  }).option("foundryUrl", {
    type: "string",
    describe: "URL for the Foundry stack"
  }).option("applicationUrl", {
    type: "string",
    describe: "URL the production application will be hosted on",
    conflicts: "skipApplicationUrl"
  }).option("skipApplicationUrl", {
    type: "boolean",
    describe: "Skip filling in URL the production application will be hosted on"
  }).option("application", {
    type: "string",
    describe: "Application resource identifier (rid)"
  }).option("ontology", {
    type: "string",
    describe: "Ontology resource identifier (rid)",
    conflicts: "skipOsdk"
  }).option("osdkPackage", {
    type: "string",
    describe: "OSDK package name for application",
    conflicts: "skipOsdk"
  }).option("skipOsdk", {
    type: "boolean",
    describe: "Skip filling in OSDK options"
  }).option("clientId", {
    type: "string",
    describe: "OAuth client ID for application"
  }).option("osdkRegistryUrl", {
    type: "string",
    describe: "URL for NPM registry to install OSDK package"
  }).option("corsProxy", {
    type: "boolean",
    describe: "Include a CORS proxy for Foundry API requests during local development"
  }).option("scopes", {
    type: "string",
    array: true,
    describe: "List of client-side scopes to be used when creating a client"
  }).check(argv => {
    if (argv.skipOsdk && (argv.sdkVersion == null || argv.sdkVersion.startsWith("1."))) {
      throw new Error("The --skipOsdk flag is only allowed when sdkVersion is 2.x. Please set --sdkVersion to 2.x or remove the --skipOsdk flag.");
    }
    return true;
  }));
  const parsed = base.parseSync();
  const project = await promptProject(parsed);
  const overwrite = await promptOverwrite({
    ...parsed,
    project
  });
  const template = await promptTemplate(parsed);
  const sdkVersion = await promptSdkVersion({
    ...parsed,
    template
  });
  const isAuthless = template.authless ?? false;
  if (isAuthless) {
    if (sdkVersion !== "2.x") {
      throw new Error(`The ${template.label} template only supports sdkVersion 2.x.`);
    }
    if (parsed.clientId != null) {
      throw new Error(`The ${template.label} template does not accept --clientId.`);
    }
    if (parsed.scopes != null) {
      throw new Error(`The ${template.label} template does not accept --scopes.`);
    }
  }
  const foundryUrl = await promptFoundryUrl(parsed);
  const applicationUrl = await promptApplicationUrl(parsed);
  const application = await promptApplicationRid(parsed);
  const clientId = isAuthless ? undefined : await promptClientId(parsed);
  const {
    osdkPackage,
    ontology,
    osdkRegistryUrl
  } = await promptOntologyAndOsdkPackageAndOsdkRegistryUrl({
    ...parsed,
    sdkVersion
  });
  const corsProxy = isAuthless ? true : await promptCorsProxy(parsed);
  const scopes = isAuthless ? undefined : await promptScopes(parsed);
  await run({
    project,
    overwrite,
    template,
    sdkVersion,
    foundryUrl,
    applicationUrl,
    application,
    clientId,
    osdkPackage,
    osdkRegistryUrl,
    corsProxy,
    scopes,
    ontology,
    unstableFeatures: parsed.unstableFeatures ?? false
  });
}